<template>
  <div id="drawingToolModeControls" class="ribbon">
    <div class="ribbon__group">
      <IconButton
        id="selectToolBtn"
        v-tooltip="'Pointer Tool (q)'"
        class="ribbon__button"
        aria-label="Pointer Tool (q)"
        :icon="'cursor'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        :class="{ 'selected': mode === 'pointer' }"
        @click="handleSetDrawingMode('pointer')"
      />
      <IconButton
        id="pointToolBtn"
        v-tooltip="!hasLabels ? 'A label is required to use this tool' : 'Point Tool (p)'"
        class="ribbon__button"
        aria-label="Point Tool (p)"
        :icon="'dotted_line'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        :class="{ 'selected': mode === 'line' }"
        :disabled="!hasLabels"
        @click="handleSetDrawingMode('line')"
      />
      <IconButton
        id="brushToolBtn"
        v-tooltip="!hasLabels ? 'A label is required to use this tool' : 'Brush Tool (w)'"
        class="ribbon__button"
        aria-label="Brush Tool (w)"
        :icon="'brush_icon'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        :class="{ 'selected': mode === 'brush' }"
        :disabled="!hasLabels"
        @click="handleSetDrawingMode('brush')"
      />
      <IconButton
        v-if="shouldEnableEraser"
        id="eraserToolBtn"
        v-tooltip="!hasLabels ? 'A label is required to use this tool' : 'Eraser Tool (e)'"
        class="ribbon__button"
        aria-label="Eraser Tool (e)"
        :icon="'eraser_icon'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        :class="{ 'selected': mode === 'eraser' }"
        :disabled="!hasLabels"
        @click="handleSetDrawingMode('eraser')"
      />
      <IconButton
        id="boxToolBtn"
        v-tooltip="!hasLabels ? 'A label is required to use this tool' : 'Box Tool (b)'"
        class="ribbon__button"
        :class="{ 'selected': mode === 'box' }"
        aria-label="2D Bounding Box Tool (b)"
        :icon="'box-tool'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        :disabled="!hasLabels"
        @click="handleSetDrawingMode('box')"
      />
      <IconButton
        v-if="isSequence"
        id="sam2ToolBtn"
        v-tooltip="!hasLabels ? 'A label is required to use this tool' : 'AI Segment Tool (a)'"
        class="ribbon__button"
        :class="{ 'selected': mode === 'sam2' }"
        aria-label="AI Segment Tool (a)"
        :icon="'sam-image'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        :disabled="!hasLabels"
        @click="confirmLaunchAGTGServer"
      />
      <IconButton
        v-else
        id="samToolBtn"
        v-tooltip="!hasLabels ? 'A label is required to use this tool' : 'AI Segment Tool (a)'"
        class="ribbon__button"
        :class="{ 'selected': mode === 'sam2' }"
        :icon="'sam-image'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        aria-label="AI Segment Tool (a)"
        :disabled="!hasLabels"
        @click="confirmLaunchAGTGServer"
      />

      <IconButton
        id="colorToolBtn"
        v-tooltip="!hasLabels ? 'A label is required to use this tool' : 'Edit Label (l)'"
        class="ribbon__button"
        :class="{ 'selected': mode === 'color' }"
        aria-label="Edit Label (l)"
        :icon="'tag'"
        :showHover="false"
        :type="''"
        :width="24"
        :height="24"
        :disabled="!hasLabels"
        @click="handleSetDrawingMode('color')"
      />
      <hr v-if="mode === 'brush' || mode === 'eraser'" class="ribbon__vr">
      <IconButton
        v-if="mode === 'brush' || mode === 'eraser'"
        id="increaseSizeBtn"
        class="ribbon__button"
        aria-label="Increase Cursor Size (=)"
        :icon="'add_box_outlined'"
        :showHover="false"
        :type="''"
        :title="'Increase Cursor Size (=)'"
        :width="24"
        :height="24"
        :disabled="!hasLabels"
        @click="increaseCursorSize"
      />
      <IconButton
        v-if="mode === 'brush' || mode === 'eraser'"
        id="decreaseSizeBtn"
        class="ribbon__button"
        aria-label="Decrease Cursor Size (-)"
        :icon="'minus_box_outlined'"
        :showHover="false"
        :type="''"
        :title="'Decrease Cursor Size (-)'"
        :width="24"
        :height="24"
        :disabled="!hasLabels"
        @click="decreaseCursorSize"
      />
    </div>
  </div>

  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="confirmButtonClass"
    :buttonText="confirmButtonText"
    @confirmed="confirmFunction"
  />
</template>

<script setup>
import IconButton from '@/components/IconButton.vue';
import { storeToRefs } from 'pinia';
import {
  ref, toRefs, watch,
} from 'vue';
import { useStore } from 'vuex';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { useSAM2Store } from '@/stores/useSAM2Store.js';
import { useEditorControlsStore } from '@/stores/useEditorControlsStore.js';

const $toast = useToast();
const store = useStore();
const props = defineProps({
  mode: { type: String, default: '' },
  size: { type: Number, default: 0 },
  imageDimensions: { type: Object, default: () => {} },
  shouldEnableEraser: { type: Boolean, default: false },
  isSequence: { type: Boolean, default: false },
  hasLabels: { type: Boolean, default: false },
});

const {
  mode,
  imageDimensions,
  shouldEnableEraser,
  hasLabels,
} = toRefs(props);

const editorControlsStore = useEditorControlsStore();
const {
  updateCursorSize,
} = editorControlsStore;

const sam2Store = useSAM2Store();
const {
  servers,
  sam2Options,
  sam2Server,
  sam2Annotations,
  aigt3dAnnotations,
  aigt2dAnnotations,
} = storeToRefs(sam2Store);

const {
  listCloudInstances,
} = sam2Store;

const confirmModal = ref(null);
const confirmMessage = ref(null);
const confirmMessageHeader = ref(null);
const confirmButtonText = ref('');
const confirmButtonClass = ref(undefined);
const confirmFunction = ref(null);

const emit = defineEmits([
  'update:mode',
]);

const drawCursorSize = ref(0);
const startingServer = ref(false);

watch(drawCursorSize, () => {
  updateCursorSize(drawCursorSize.value);
});

watch(mode, () => {
  if (!hasLabels.value) {
    handleSetDrawingMode('pointer');
  }
}, { immediate: true });

watch(imageDimensions, (dimensions) => {
  if (Object.keys(dimensions).length > 0) {
    drawCursorSize.value = 0.04 * dimensions.width;
  }
}, { deep: true });

function increaseCursorSize() {
  if (drawCursorSize.value < (0.2 * imageDimensions.value.width)) {
    drawCursorSize.value *= 1.3;
  }
}
function decreaseCursorSize() {
  if (drawCursorSize.value > (0.001 * imageDimensions.value.width)) {
    drawCursorSize.value /= 1.3;
  }
}

async function confirmLaunchAGTGServer() {
  await handleRefreshServers();

  if (servers.value && servers.value.length > 0) {
    sam2Server.value = servers.value[0];
    handleSetDrawingMode('sam2');
  } else {
    confirmMessage.value = 'To use these advanced features, an Automated Ground Truth Generation (AGTG) server must be launched. This will start a dedicated GPU server instance and will incur additional charges based on usage.<br><br>The server will terminate automatically after 15 minutes of inactivity.';
    confirmMessageHeader.value = 'Launch AGTG Server';
    confirmButtonText.value = 'Launch AGTG Server';
    confirmButtonClass.value = undefined;
    confirmModal.value.showModal();
    confirmFunction.value = () => { launchAGTGServer(); };
  }
}

async function launchAGTGServer() {
  startingServer.value = true;
  const params = {
    project_id: store.state.projects.currentProject.id,
    name: 'AI Assisted Ground Truth',
    type: 'aigt',
  };
  const dataConnect = new DatastoreConnect();
  await dataConnect.startCloudInstance(params)
    .then(async (data) => {
      if (data.result && !data.error) {
        // $toast.open({
        //   queue: true,
        //   duration: 5000,
        //   message: `Server ${name.value} started successfully.`,
        //   type: 'success',
        //   position: 'top',
        // });
        await handleRefreshServers(data.result);
        if (servers.value && sam2Server.value) {
          handleSetDrawingMode('sam2');
        }
      } else {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Server failed to start: ${data.error.message}`,
          type: 'error',
          position: 'top',
        });
      }
      startingServer.value = false;
    })
    .catch((e) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Server failed to start: ${e.message}`,
        type: 'error',
        position: 'top',
      });
      startingServer.value = false;
    });
}

async function handleRefreshServers(createServerResult) {
  servers.value = await listCloudInstances();
  if (servers.value.length > 0 && createServerResult && createServerResult.cloud_instance_id) {
    const foundServer = servers.value.find((x) => x.id === createServerResult.cloud_instance_id);
    if (foundServer) {
      sam2Server.value = foundServer;
    }
  }
}

function handleSetDrawingMode(newDrawingMode) {
  if (mode.value === 'sam2' && sam2Annotations.value && sam2Annotations.value.length > 0) {
    confirmMessage.value = 'There are unsaved annotations. Switching to a different tool will clear all unsaved annotations. Do you wish to continue?';
    confirmMessageHeader.value = 'Unsaved Changed';
    confirmButtonText.value = 'Continue';
    confirmButtonClass.value = undefined;
    confirmModal.value.showModal();
    confirmFunction.value = () => {
      sam2Options.value.params.clicks = [];
      sam2Options.value.params.box = [];
      sam2Options.value.params.boxes = [];
      sam2Annotations.value = [];
      aigt3dAnnotations.value = [];
      aigt2dAnnotations.value = [];
      emit('update:mode', newDrawingMode);
    };
  } else {
    emit('update:mode', newDrawingMode);
  }
}

defineExpose({
  confirmLaunchAGTGServer,
});

</script>

<style lang="scss" scoped>
.ribbon {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  flex-direction: column;
  width: fit-content;
  width: 40px;
  padding: 16px 2px;
  justify-content: space-between;
  font-weight: 500;
  box-shadow: 0 0 3px gray, 0 0 10px gray;
  border-radius: 4px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  z-index: 5;
  align-items: center;
  @include themify() {
    color: themed('ribbon-icons-color');
    background: themed('ribbon-bg-transparent');
  }

  &__group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  &__vr {
    display: flex;
    width: 100%;
    height: 1px;
    margin-top: 5px;
  }

  &__button {
    display: flex;
    min-width: 22px;
    height: 22px;
    color: inherit;
    background: none;
    border: none;
    font-size: 0.875rem;
    line-height: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .05em;
    align-items: center;
    cursor: pointer;

    &:hover:not(:disabled), &.active:not(:disabled) {
      @include themify() {
        box-shadow: 0 0 0 4px themed('icon-hover-color');
        background-color: themed('icon-hover-color');
      }
      border-radius: 4px;
    }

    &:disabled {
      cursor: default;
      @include themify() {
        color: themed('color-disabled');
      }
    }
  }

  &__button + &__button {
      margin-top: 5px;
  }

  .icon-button + .icon-button {
      margin-left: 0px !important;
  }

  .selected {
      @include themify() {
        box-shadow: 0 0 0 4px hsl(0, 1%, 66%);
        background-color: hsl(0, 1%, 66%);
      }
      border-radius: 4px;
    }
}

</style>
